import { useState, useEffect } from 'react';
import { IconRefresh, IconDeviceFloppy, IconLoader2 } from '@tabler/icons-react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface PagePermission {
  page: string;
  canRead: boolean;
  canWrite: boolean;
}

interface RolePermissionsData {
  permissions: Record<string, PagePermission[]>;
  pages: string[];
}

const ROLE_BADGE_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'success' | 'warning' | 'info' | 'outline' | 'ghost'> = {
  admin: 'default',
  manager: 'outline',
  support: 'info',
  viewer: 'secondary',
  finance: 'warning',
  'account-director': 'success',
  'campaign-editor': 'ghost',
};

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  manager: 'Manager',
  support: 'Support',
  viewer: 'Viewer',
  finance: 'Finance',
  'account-director': 'Account Director',
  'campaign-editor': 'Campaign Editor',
};

const PAGE_LABELS: Record<string, string> = {
  'ai-sql': 'AI Data Assistant',
  'assign-esims': 'Assign eSIMs',
  'campaigns': 'Campaigns',
  'campaigns-brand-groups': 'Campaign Brand Groups',
  'churn': 'Churn',
  'customers': 'Customers',
  'customer-care-actions': 'Customer Care Actions',
  'customer-lifecycle': 'Customer Suspend / Unsuspend',
  'cycle': 'Cycle',
  'dashboard': 'Dashboard',
  'dev-tools': 'Dev Tools',
  'distributors': 'Distributors',
  'email-history': 'Email History',
  'entities': 'Compliance',
  'honey-connect': 'Honey Connect Agent',
  'innerbox': 'Innerbox Management',
  'iot': 'IoT',
  'monitoring': 'Monitoring',
  'mtn': 'MTN',
  'people': 'People',
  'products': 'Products',
  'promo-data': 'Promo Data',
  'reports': 'Reports',
  'shopify': 'Shopify',
  'sim-cards': 'SIM Cards',
  'sim-import': 'SIM Card Import',
  'sim-stock': 'SIM Stock',
  'spar-mobile': 'SPAR Mobile',
  'surveys': 'Surveys',
  'transactions': 'Transactions',
  'user-management': 'User Management',
  'vouchers': 'Vouchers',
  'wallet': 'Wallet',
  'xero': 'Xero',
};

export function RolePermissionsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actualUser, setActualUser] = useState<any>(null);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [data, setData] = useState<RolePermissionsData>({ permissions: {}, pages: [] });
  const [permissions, setPermissions] = useState<Record<string, Record<string, PagePermission>>>({});

  useEffect(() => {
    checkUserAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkUserAccess = async () => {
    try {
      const res = await api.get('/users/me');
      setActualUser(res.data);
      const isAdmin = res.data.roles?.includes('admin') || res.data.role === 'admin';
      if (isAdmin) {
        loadPermissions();
      }
    } catch (err) {
      console.error('Failed to check user access', err);
    } finally {
      setCheckingAccess(false);
    }
  };

  const loadPermissions = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/users/role-permissions');
      setData(res.data);

      const permsMap: Record<string, Record<string, PagePermission>> = {};

      Object.keys(ROLE_LABELS).forEach((role) => {
        permsMap[role] = {};
      });

      Object.entries(res.data.permissions || {}).forEach(([role, perms]) => {
        permsMap[role] = {};
        (perms as PagePermission[]).forEach((perm) => {
          permsMap[role][perm.page] = { ...perm };
        });
      });

      res.data.pages.forEach((page: string) => {
        Object.keys(ROLE_LABELS).forEach((role) => {
          if (!permsMap[role][page]) {
            permsMap[role][page] = { page, canRead: false, canWrite: false };
          }
        });
      });

      setPermissions(permsMap);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (role: string, page: string, field: 'canRead' | 'canWrite', value: boolean) => {
    setPermissions((prev) => {
      const newPerms = { ...prev };
      if (!newPerms[role]) newPerms[role] = {};
      if (!newPerms[role][page]) newPerms[role][page] = { page, canRead: false, canWrite: false };
      newPerms[role][page] = { ...newPerms[role][page], [field]: value };
      if (field === 'canWrite' && value) newPerms[role][page].canRead = true;
      if (field === 'canRead' && !value) newPerms[role][page].canWrite = false;
      return newPerms;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const updates: Array<{ role: string; permissions: PagePermission[] }> = [];

      Object.entries(permissions).forEach(([role, rolePerms]) => {
        const permsArray = Object.values(rolePerms).filter((p) => p.canRead || p.canWrite);
        if (permsArray.length > 0) {
          updates.push({ role, permissions: permsArray });
        }
      });

      await Promise.all(
        updates.map(({ role, permissions: perms }) =>
          api.put(`/users/role-permissions/${role}`, { permissions: perms })
        )
      );

      setSuccess('Permissions saved successfully');
      loadPermissions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const formatPageName = (page: string) => {
    return PAGE_LABELS[page] || page
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (checkingAccess) {
    return (
      <div className="flex justify-center items-center min-h-[400px] p-6">
        <IconLoader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const isAdmin = actualUser?.roles?.includes('admin') || actualUser?.role === 'admin';
  if (!actualUser || !isAdmin) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>
            Access denied. Only administrators can access role permissions management.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const roles = Object.keys(ROLE_LABELS);

  return (
    <div>
      <PageHeader
        title="Role Permissions Management"
        subtitle="Configure which pages each role can access with read/write permissions"
        action={
          <div className="flex gap-2">
            <Button variant="outline" size="icon" onClick={loadPermissions} disabled={loading} title="Refresh">
              <IconRefresh className="h-4 w-4" />
            </Button>
            <Button onClick={handleSave} disabled={saving || loading}>
              <IconDeviceFloppy className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save All Changes'}
            </Button>
          </div>
        }
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="flex items-center justify-between">
            {error}
            <button onClick={() => setError('')} className="text-sm underline ml-4">Dismiss</button>
          </AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-4">
          <AlertDescription className="flex items-center justify-between">
            {success}
            <button onClick={() => setSuccess('')} className="text-sm underline ml-4">Dismiss</button>
          </AlertDescription>
        </Alert>
      )}

      <Alert className="mb-6">
        <AlertDescription>
          <strong>Note:</strong> Users with multiple roles will receive the union of all permissions from their assigned roles.
          If a user has read access from one role and write access from another, they will have both.
        </AlertDescription>
      </Alert>

      {loading ? (
        <div className="flex justify-center py-8">
          <IconLoader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <MainCard>
          <div className="max-h-[calc(100vh-380px)] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[200px] font-bold">Page</TableHead>
                  {roles.map((role) => (
                    <TableHead key={role} className="text-center min-w-[150px]">
                      <Badge variant={ROLE_BADGE_VARIANTS[role] ?? 'secondary'} className="text-xs">
                        {ROLE_LABELS[role]}
                      </Badge>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.pages.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={roles.length + 1} className="text-center py-8">
                      No pages found. Please run the database migration first.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.pages.map((page) => (
                    <TableRow key={page} className="hover:bg-muted/50">
                      <TableCell>
                        <p className="text-sm font-medium">{formatPageName(page)}</p>
                        <p className="text-xs text-muted-foreground">{page}</p>
                      </TableCell>
                      {roles.map((role) => {
                        const perm = permissions[role]?.[page] || { page, canRead: false, canWrite: false };
                        return (
                          <TableCell key={role} className="text-center">
                            <div className="flex flex-col gap-2 items-center">
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-muted-foreground">Read</span>
                                <Checkbox
                                  checked={perm.canRead}
                                  onCheckedChange={(checked) =>
                                    handlePermissionChange(role, page, 'canRead', checked === true)
                                  }
                                />
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-muted-foreground">Write</span>
                                <Checkbox
                                  checked={perm.canWrite}
                                  disabled={!perm.canRead}
                                  onCheckedChange={(checked) =>
                                    handlePermissionChange(role, page, 'canWrite', checked === true)
                                  }
                                />
                              </div>
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <Alert variant="warning" className="mt-4">
              <AlertDescription>
                <p className="font-bold mb-1">Important:</p>
                <p className="text-sm">
                  • Changes are saved to the database immediately when you click "Save All Changes"<br />
                  • Users will see updated permissions on their next page load or login<br />
                  • Write access automatically requires read access (cannot have write without read)<br />
                  • Disabling read access will automatically disable write access
                </p>
              </AlertDescription>
            </Alert>
          </div>
        </MainCard>
      )}
    </div>
  );
}
